import {Button, Modal} from 'antd'
import {curriculum_getByOwner_Api} from 'apis'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {CurriculumActionTypes} from 'views/AppLocals/Course'
import {
    CreateCurriculumModal,
    EditCurriculumModal,
    EditSubjectParamModal,
    LinkSubjectModal,
} from 'views/AppLocals/Course/Curriculum/CurriculumForm'
import {useCurriculum} from 'views/AppLocals/Course/Curriculum/useCurriculum'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault, renderIf} from 'views/Shared'
import {renderCurriculum} from '../functions/renderCurriculum'
import {BiLink, BiSolidEdit, BiTrash, BiUnlink} from 'react-icons/bi'
import {RiSearchEyeLine} from 'react-icons/ri'
import {GoDotFill} from 'react-icons/go'
import getTitle from 'helpers/getTitle'
import LoadingBlock from "../../../Loading/LoadingBlock";

// ─── Subject list inside a curriculum detail ──────────────────────────────────

const CurriculumSubjectRow = ({item, onUnlink, onEditParam}) => {
    const t = useTranslate()
    const {title, number_of_sessions, is_required, passed_score} = item ?? {}
    const is_mandatory = is_required
    return (
        <div className="py-2 px-3 flex items-start justify-between gap-3 border-b border-color-50 last:border-0">
            <div className="flex flex-col gap-1 flex-1">
                <span className="font-medium text-sm text-color-100">{title}</span>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {renderIf(
                        number_of_sessions > 0,
                        <span className="text-xs text-color-400 italic">
                            {`${t('sessions')}: ${number_of_sessions}`}
                        </span>
                    )}
                    <span className={`text-xs italic ${is_mandatory ? 'text-red-500' : 'text-color-400'}`}>
                        {is_mandatory ? t('mandatory') : t('optional')}
                    </span>
                    {renderIf(
                        passed_score !== undefined && passed_score !== null,
                        <span className="text-xs text-color-400 italic">
                            {`${t('passed score')}: ${passed_score}`}
                        </span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <Button
                    size="small" type="text"
                    icon={<BiSolidEdit size={14} className="text-blue-500"/>}
                    onClick={() => onEditParam(item)}
                />
                <Button
                    size="small" type="text"
                    icon={<BiUnlink size={14} className="text-red-400"/>}
                    onClick={() => onUnlink(item)}
                />
            </div>
        </div>
    )
}

const CurriculumDetailContent = ({item, onUnlink, onEditParam, onLinkSubject}) => {
    const t = useTranslate()
    const {description, duration_in_months, idname} = item ?? {}

    return (
        <div className="flex flex-col gap-4 p-2">
            <div className="flex flex-col gap-2 border border-color-50 rounded-lg p-3 bg-background">
                {renderIf(
                    description,
                    <p className="text-sm text-color-300 tracking-wide">{description}</p>
                )}
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                    {renderIf(
                        duration_in_months > 0,
                        <div className="flex items-center gap-2">
                            <GoDotFill size={8} className="text-color-400"/>
                            <span className="text-xs text-color-400 italic">
                                {`${t('duration')}: `}
                            </span>
                            <span className="px-2 border border-blue-400 bg-blue-50 rounded-full text-xs font-medium text-blue-500">
                                {`${duration_in_months} ${t('months')}`}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm text-color-100 uppercase tracking-wide">
                    {t('subjects')}
                </h3>
                <Button
                    size="small"
                    icon={<BiLink size={14}/>}
                    onClick={() => onLinkSubject(item)}>
                    {t('link subject')}
                </Button>
            </div>
            {(() => {
                const subjects = item?.subjects || []
                if (!subjects.length) {
                    return (
                        <div className="py-4 text-center text-sm text-color-400 italic">
                            {t('no subjects linked')}
                        </div>
                    )
                }
                return (
                    <div className="flex flex-col divide-y divide-color-50 border border-color-50 rounded-lg overflow-hidden">
                        {subjects.map((s, i) => (
                            <CurriculumSubjectRow
                                key={s.id || i}
                                item={s}
                                onUnlink={onUnlink}
                                onEditParam={onEditParam}
                            />
                        ))}
                    </div>
                )
            })()}
        </div>
    )
}

// ─── Main container ───────────────────────────────────────────────────────────

const CurriculaManager = ({owner_id, owner_type,                        filter_actions = {
    sortBy: 'sort-by',
    keyword: 'keyword',
},
                          }) => {
    const t = useTranslate()
    const location = useLocation()

    const [action, setAction] = useState({type: undefined, value: undefined})
    const onCancel = () => setAction({})

    const {
        refreshKey,
        onCreate = NullAsDefault,
        onUpdate = NullAsDefault,
        onDelete = NullAsDefault,
        onLinkSubject = NullAsDefault,
        onUnlinkSubject = NullAsDefault,
        onUpdateSubjectParam = NullAsDefault,
    } = useCurriculum({
        onCreated: onCancel,
        onUpdated: onCancel,
        onDeleted: onCancel,
        onLinked: onCancel,
        onUnlinked: onCancel,
        onSubjectParamUpdated: onCancel,
    })

    const confirmDelete = (item) => {
        const id = getId(item) || item?.idname
        if (id) {
            showDeleteConfirmDialog({
                translate: t,
                title: 'delete curriculum',
                message: 'are you sure you want to delete this curriculum',
                onOk: () => onDelete({}, {id}),
            })
        }
    }

    const confirmUnlink = (item) => {
        const id = item?.id
        if (id) {
            showDeleteConfirmDialog({
                translate: t,
                title: 'unlink subject',
                message: 'are you sure you want to unlink this subject from curriculum',
                onOk: () => onUnlinkSubject({}, {id}),
            })
        }
    }

    const handleAction = (type, value) => {
        switch (type) {
            case CurriculumActionTypes.CURRICULUM_READ:
            case CurriculumActionTypes.CURRICULUM_CREATE:
            case CurriculumActionTypes.CURRICULUM_UPDATE:
            case CurriculumActionTypes.CURRICULUM_LINK_SUBJECT:
            case CurriculumActionTypes.CURRICULUM_UNLINK_SUBJECT:
                setAction({type, value})
                break
            case CurriculumActionTypes.CURRICULUM_DELETE:
                confirmDelete(value)
                break
            default:
                break
        }
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <RiSearchEyeLine size={14} className="text-green-600"/>,
                        label: 'view detail',
                        key: CurriculumActionTypes.CURRICULUM_READ,
                    },
                    {
                        icon: <BiLink size={14} className="text-blue-500"/>,
                        label: 'link subject',
                        key: CurriculumActionTypes.CURRICULUM_LINK_SUBJECT,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: CurriculumActionTypes.CURRICULUM_UPDATE,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: CurriculumActionTypes.CURRICULUM_DELETE,
                    },
                ],
                (key) => handleAction(key, item)
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    const modal = useMemo(() => {
        const id = action?.value?.idname || getId(action?.value)

        let title
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({initialValues: {}})
        let modal_params = {}

        switch (action?.type) {
            case CurriculumActionTypes.CURRICULUM_READ:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {getTitle(action?.value) || t('curriculum detail')}
                    </div>
                )
                modal_params = {width: 640, footer: null}
                Content = ({initialValues}) => (
                    <CurriculumDetailContent
                        item={initialValues || action?.value}
                        onUnlink={confirmUnlink}
                        onEditParam={(subjectItem) =>
                            setAction({
                                type: CurriculumActionTypes.CURRICULUM_UNLINK_SUBJECT,
                                value: subjectItem,
                            })
                        }
                        onLinkSubject={(curr) =>
                            setAction({
                                type: CurriculumActionTypes.CURRICULUM_LINK_SUBJECT,
                                value: curr,
                            })
                        }
                    />
                )
                Wrapper = ({children}) =>
                    children({initialValues: action?.value, isLoading: false})
                break

            case CurriculumActionTypes.CURRICULUM_CREATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('create curriculum')}
                    </div>
                )
                modal_params = {width: 600}
                Content = () => (
                    <CreateCurriculumModal
                        owner_id={owner_id}
                        owner_type={owner_type}
                        onCancel={onCancel}
                        onSubmit={(values) =>
                            onCreate(values, {type: owner_type, id: owner_id})
                        }
                    />
                )
                Wrapper = ({children}) => children({initialValues: {}, isLoading: false})
                break

            case CurriculumActionTypes.CURRICULUM_UPDATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit curriculum')}
                    </div>
                )
                modal_params = {width: 600}
                Content = ({initialValues}) => (
                    <EditCurriculumModal
                        owner_id={owner_id}
                        owner_type={owner_type}
                        initialValues={initialValues}
                        onCancel={onCancel}
                        onSubmit={(values) => onUpdate(values, {id})}
                    />
                )
                Wrapper = ({children}) =>
                    children({initialValues: action?.value, isLoading: false})
                break

            case CurriculumActionTypes.CURRICULUM_LINK_SUBJECT:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('link subject to curriculum')}
                    </div>
                )
                modal_params = {width: 560}
                Content = () => (
                    <LinkSubjectModal
                        curriculum_id={id}
                        owner_id={owner_id}
                        owner_type={owner_type}
                        onCancel={onCancel}
                        onSubmit={(params, {curriculum_id, subject_id}) =>
                            onLinkSubject(params, {curriculum_id, subject_id})
                        }
                    />
                )
                Wrapper = ({children}) => children({initialValues: {}, isLoading: false})
                break

            case CurriculumActionTypes.CURRICULUM_UNLINK_SUBJECT:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit subject parameters')}
                    </div>
                )
                modal_params = {width: 480}
                Content = ({initialValues}) => (
                    <EditSubjectParamModal
                        initialValues={initialValues}
                        onCancel={onCancel}
                        onSubmit={(values) =>
                            onUpdateSubjectParam(values, {id: action?.value?.id})
                        }
                    />
                )
                Wrapper = ({children}) =>
                    children({initialValues: action?.value, isLoading: false})
                break

            default:
                break
        }

        return (
            <Modal
                footer={null}
                title={title}
                onCancel={onCancel}
                open={!!action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({isLoading, initialValues}) => {
                        if (isLoading) return <LoadingBlock/>
                        return <Content initialValues={initialValues}/>
                    }}
                </Wrapper>
            </Modal>
        )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, owner_id, owner_type])

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            refreshKey,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, refreshKey, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            // onSortChange(sortBy)
            onFilterChange({

            })
        },
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            renderItem: renderCurriculum({
                translate: t,
                renderContextMenu,
                handleAction,
            }),
            apiInfo: curriculum_getByOwner_Api,
            query: bindQueryParams([{type: owner_type}, {id: owner_id}]),
            renderWrap: (child) => (
                <div className="mt-2 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
        },
        renderFilter: NullAsDefault,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        dependencies: [owner_id, owner_type],
        withKey: createPagerKey(refresh_deps),
        query_params: filter_actions,
    })

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('curriculum')}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <div className="mt-1 text-sm text-color-300 tracking-wider">
                        {t('overall structure for each type — used to build courses and track progress')}
                    </div>
                </div>
                <Button
                    color="primary" variant="solid"
                    className="px-6 py-2 font-semibold rounded-lg"
                    onClick={() => handleAction(CurriculumActionTypes.CURRICULUM_CREATE)}>
                    {t('create curriculum')}
                </Button>
            </div>
            {pager_widget}
            {modal}
            <div className="h-16"/>
        </div>
    )
}

const Curricula = compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(CurriculaManager)

export default Curricula
